import { DestroyRef, effect, inject, Service, signal } from '@angular/core';
import { Store } from '@ngxs/store';
import { colorSchemeStore, initColorScheme, setColorScheme } from '@digitale-menschen/component-library';
import { ProfileState } from '../states/profile/profile-state';

export type ColorSchemeChoice = 'light' | 'dark' | 'system';

/**
 * Light/dark/system switch for Neosys users only; everyone else stays light whatever their OS prefers.
 * The choice lives under its own key, which `index.html` also reads, since the library stores «system» as no key.
 */
@Service()
export class ColorSchemeService {
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly profile = this.store.selectSignal(ProfileState.profile);
  private readonly storageKey = 'lp-color-scheme';

  /** Whether the signed-in user may switch; the account menu shows the choice only then. */
  public readonly isSwitchAvailable = this.store.selectSignal(ProfileState.isAdmin);

  private readonly choiceState = signal<ColorSchemeChoice>('light');
  public readonly choice = this.choiceState.asReadonly();

  private isHelperStarted = false;

  /**
   * On every profile change: a signed-in Neosys user gets the stored choice, anyone else sees light, on this
   * page only, so a Neosys user of the same browser keeps their choice.
   */
  private readonly profileEffect = effect(() => {
    if (!this.profile()?.istNeosysAdmin) {
      this.keepPageLight();
      return;
    }
    const storedChoice = this.readStoredChoice();
    if (storedChoice) {
      this.applyChoice(storedChoice);
    }
  });

  /**
   * Applies the stored choice when the restored profile is a Neosys user's. Called once per app from an
   * application initializer; {@link profileEffect} covers a profile that loads later.
   */
  public initialize(): void {
    const storageListener = (event: StorageEvent): void => this.followChoiceFromOtherTab(event);
    window.addEventListener('storage', storageListener);
    this.destroyRef.onDestroy(() => window.removeEventListener('storage', storageListener));
    const storedChoice = this.readStoredChoice();
    if (!storedChoice || !this.profile()?.istNeosysAdmin) {
      return;
    }
    this.applyChoice(storedChoice);
  }

  public setChoice(choice: ColorSchemeChoice): void {
    this.writeStoredChoice(choice);
    this.applyChoice(choice);
  }

  /** A tab that started without a choice has no helper yet to hear one made in another tab. */
  private followChoiceFromOtherTab(event: StorageEvent): void {
    if (event.key !== this.storageKey || !this.profile()?.istNeosysAdmin) {
      return;
    }
    const storedChoice = this.readStoredChoice();
    if (storedChoice) {
      this.applyChoice(storedChoice);
    }
  }

  private applyChoice(choice: ColorSchemeChoice): void {
    setColorScheme(choice === 'system' ? 'auto' : choice);
    this.choiceState.set(choice);
    this.startHelper();
  }

  /** Keeps {@link choice} in step with the helper, a change from another tab included. */
  private startHelper(): void {
    if (this.isHelperStarted) {
      return;
    }
    this.isHelperStarted = true;
    initColorScheme();
    colorSchemeStore.subscribe(({ scheme, resolved }) => {
      this.choiceState.set(scheme === 'auto' ? 'system' : scheme);
      if (resolved === 'dark' && !this.profile()?.istNeosysAdmin) {
        this.keepPageLight();
      }
    });
  }

  /**
   * Shows light on this page only, for anyone but a signed-in Neosys user: resetting the shared helper state
   * would switch a Neosys user's tabs back as well.
   */
  private keepPageLight(): void {
    document.documentElement.classList.remove('dark-mode');
    const colorSchemeMeta = document.head.querySelector<HTMLMetaElement>('meta[name="color-scheme"]');
    if (colorSchemeMeta) {
      colorSchemeMeta.content = 'light';
    }
  }

  private readStoredChoice(): ColorSchemeChoice | null {
    try {
      const storedValue = window.localStorage.getItem(this.storageKey);
      return storedValue === 'light' || storedValue === 'dark' || storedValue === 'system' ? storedValue : null;
    } catch {
      return null;
    }
  }

  private writeStoredChoice(choice: ColorSchemeChoice): void {
    try {
      window.localStorage.setItem(this.storageKey, choice);
    } catch {
      // Storage blocked (private mode): the choice lasts for this page only.
    }
  }
}
